import {
  Component,
  Input,
  OnInit,
  ElementRef,
  ViewChild,
  forwardRef,
  Output,
  EventEmitter
} from '@angular/core';
import {
  ControlValueAccessor,
  NG_VALUE_ACCESSOR
} from '@angular/forms';
import * as moment from 'moment';
import 'moment-timezone';

/** 
 * Maps specific format tokens to moment units.
 */
const TOKEN_TO_MOMENT_UNIT: Record<string, moment.unitOfTime.DurationConstructor> = {
  'YYYY': 'year',
  'YY': 'year',
  'MM': 'month',
  'M': 'month',
  'DD': 'day',
  'D': 'day',
  'HH': 'hour',
  'H': 'hour',
  'hh': 'hour',
  'h': 'hour',
  'mm': 'minute',
  'm': 'minute',
  'ss': 'second',
  's': 'second'
};

interface FormatSegment {
  token: string;
  start: number;
  end: number;
  unit?: moment.unitOfTime.DurationConstructor;
}

@Component({
  selector: 'app-date-time-input',
  template: `
    <input
      #inputEl
      type="text"
      [mask]="mask"
      [dropSpecialCharacters]="false"
      [placeholder]="dateFormat"
      [(ngModel)]="displayValue"
      [class]="classes"
      [style]="styles"
      [ngClass]="classList"
      [ngStyle]="styleList"
      (keydown)="onKeyDown($event)"
      [tabIndex]="tIndex"
      [attr.tabIndex]="tIndex"
      (blur)="handleBlur($event)"
      (focus)="handleFocus($event)"
    />
  `,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DateTimeInputComponent),
      multi: true
    }
  ]
})
export class DateTimeInputComponent implements ControlValueAccessor, OnInit {
  constructor() {}
  formControlDateFormat='YYYY-MM-DD'
  @Input() dateFormat: string = 'DD/MM/YYYY';
  @Input() minDate?: Date | string;
  @Input() maxDate?: Date | string;
  @Input() fieldController?: any;
  @Input() classes?: any;
  @Input() styles?: any;
  @Input() tIndex?: any;
  @Input('dynamicClasses') classList: any;
  @Input('dynamicStles') styleList: any;

  @Output() change = new EventEmitter<string | null>();
  @Output() blur = new EventEmitter<FocusEvent>();
  @Output() focus = new EventEmitter<FocusEvent>();

  mask: string = '';
  private _displayValue = '';
  @ViewChild('inputEl', { static: true }) inputRef!: ElementRef<HTMLInputElement>;

  private onChange: (val: string | null) => void = () => {};
  private onTouched: () => void = () => {};

  private segments: FormatSegment[] = [];

  /* ------------------ Get/Set ------------------ */
  get displayValue(): string {
    return this._displayValue;
  }

  set displayValue(val: string) {
    this._displayValue = val;
  
    const parsed = moment(val, this.dateFormat, true);
    const min = this.getMinDate();
    const max = this.getMaxDate();
  
    if (parsed.isValid()) {
      const isBeforeMin = min && parsed.isBefore(min);
      const isAfterMax = max && parsed.isAfter(max);
  
      if (isBeforeMin || isAfterMax) {
        this.onChange(null); // emit invalid
      } else {
        const formatted = parsed.format(this.formControlDateFormat);
        this.onChange(formatted); 
        this.change?.emit(formatted); 
      }
    } else {
      this.onChange(null);
    }
  
    if (this.fieldController?.updateValueAndValidity) {
      this.fieldController.updateValueAndValidity({ emitEvent: false });
    }
  }

  writeValue(value: moment.Moment | Date | string | null): void {
     if (!value) {
    this.displayValue = '';
    return;
  }
    let parsed: moment.Moment | null = null;
    
    if (moment.isMoment(value)) {
      parsed = value;
    } else if (value instanceof Date && !isNaN(value.getTime())) {
      parsed = moment(value);
    } else if (typeof value === 'string') {
      parsed = moment(value, this.formControlDateFormat, true);
      if(!parsed?.isValid() && this.segments.length > 3){
        parsed = moment(value, 'YYYY-MM-DDTHH:mm', true);
      }
    }
    this.displayValue = parsed?.isValid()
      ? parsed.format(this.dateFormat)
      : '';
  }

  registerOnChange(fn: (val: string | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = () => {
      fn();
      if (this.fieldController?.updateValueAndValidity) {
        this.fieldController.updateValueAndValidity({ emitEvent: false });
      }
    };
  }

  setDisabledState(isDisabled: boolean): void {
    this.inputRef.nativeElement.disabled = isDisabled;
  }

  ngOnInit(): void {
    this.parseDateTimeFormat(this.dateFormat);
    this.mask = this.buildMaskFromFormat(this.dateFormat);
  }

  /* ------------------ Arrow Key Handling ------------------ */
  onKeyDown(event: KeyboardEvent) {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault();
      const step = event.key === 'ArrowUp' ? 1 : -1;
      const inputEl = this.inputRef.nativeElement;
      const caretPos = inputEl.selectionStart ?? 0;

      const segIndex = this.segments.findIndex(
        seg => caretPos >= seg.start && caretPos <= seg.end
      );
      if (segIndex === -1) return;

      const seg = this.segments[segIndex];
      if (!seg.unit) return;

      let parsed = moment(this.displayValue, this.dateFormat, true);
      let hasValue = true;
      if (!parsed.isValid()) {
        parsed = moment(); // fallback
        hasValue = false
      }

      const updated = hasValue ? parsed.add(step, seg.unit) : parsed;
      const min = this.getMinDate();
      const max = this.getMaxDate();

      if ((min && updated.isBefore(min)) || (max && updated.isAfter(max))) {
        return; // out of bounds
      }

      this.displayValue = updated.format(this.dateFormat);

      const distFromSegmentStart = caretPos - seg.start;
      let newCaret = seg.start + distFromSegmentStart;
      if (newCaret >= seg.end) {
        newCaret = seg.end - 1;
      }

      setTimeout(() => {
        inputEl.setSelectionRange(newCaret, newCaret);
        if (this.fieldController?.updateValueAndValidity) {
          this.fieldController.updateValueAndValidity({ emitEvent: false });
        }
      });
    }
  }

    /* ------------------ Additional Event Handlers ------------------ */
    handleBlur(event: FocusEvent): void {
      this.onTouched();
    
      // Patch the event to include internal format in target.value
      const parsed = moment(this.displayValue, this.dateFormat, true);
    
      if (parsed.isValid()) {
        const formatted = parsed.format(this.formControlDateFormat);
    
        // Clone the event and override .target.value
        const clonedTarget = {
          ...event.target,
          value: formatted
        } as HTMLInputElement;
    
        const clonedEvent = {
          ...event,
          target: clonedTarget,
          currentTarget: clonedTarget
        } as FocusEvent;
    
        this.blur.emit(clonedEvent);
      } else {
        this.blur.emit(event); // fallback
      }
    }
  
    handleFocus(event: FocusEvent): void {
      this.focus.emit(event);
    }

  /* ------------------ Helpers ------------------ */
  private getMinDate(): moment.Moment | null {
    return this.minDate ? moment(this.minDate, this.formControlDateFormat) : null;
  }

  private getMaxDate(): moment.Moment | null {
    return this.maxDate ? moment(this.maxDate) : null;
  }

  private parseDateTimeFormat(fmt: string): void {
    this.segments = [];
    let currentIndex = 0;
    let buffer = '';

    for (let i = 0; i < fmt.length; i++) {
      const c = fmt[i];
      if (/[A-Za-z]/.test(c)) {
        buffer += c;
      } else {
        if (buffer.length > 0) {
          this.pushSegment(buffer, currentIndex);
          currentIndex += buffer.length;
          buffer = '';
        }
        currentIndex += 1;
      }
    }

    if (buffer.length > 0) {
      this.pushSegment(buffer, currentIndex);
    }
  }

  private pushSegment(token: string, startIndex: number): void {
    const endIndex = startIndex + token.length;
    const segment: FormatSegment = { token, start: startIndex, end: endIndex };
    const unit = TOKEN_TO_MOMENT_UNIT[token];
    if (unit) segment.unit = unit;
    this.segments.push(segment);
    if(this.segments.length > 3) this.formControlDateFormat = 'YYYY-MM-DD HH:mm'
    if(this.dateFormat == 'HH:mm'){
      this.formControlDateFormat = 'HH:mm'
    }
  }

  private buildMaskFromFormat(fmt: string): string {
    return fmt.replace(/[A-Za-z]/g, '0');
  }
}
